// 3D-Drucker: Stand aus internal/bambu. GET /v2-api/drucker ist
// Boot-Hydration und Reconciliation; der laufende Stand kommt per WS
// "drucker" (der Dienst publiziert jede Änderung, höchstens einmal je
// Sekunde) -- in derselben Form wie der Endpoint.
//
// SWR: ja -- beim Reload steht sofort der letzte Stand statt „lädt".
// Der Verlauf der Aufträge kommt aus /v2-api/drucker/auftraege und wird
// neu geholt, sobald ein Auftrag endet (der Zustand wechselt).

import { SvelteMap } from "svelte/reactivity";

import { getJSON, postJSON, putJSON } from "./api";
import type { Auftrag, Stand } from "./drucker/drucker";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { registerRevalidator } from "./revalidate.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

const STORAGE_KEY = "drucker";
const ABGLEICH_MS = 60_000;

const _stand = new SvelteMap<string, Stand>();
const _auftraege = new SvelteMap<string, Auftrag[]>();
let _started = false;
let _timer: ReturnType<typeof setInterval> | undefined;
let _unsubscribe: (() => void) | undefined;

function uebernimm(s: Stand): void {
  const vorher = _stand.get("current");
  _stand.set("current", s);
  persist(STORAGE_KEY, s);
  // Ein Auftrag ist zu Ende gegangen (oder ein neuer beginnt): Verlauf neu.
  if (_auftraege.has("current") && vorher?.druck?.zustand !== s.druck?.zustand) {
    void ladeAuftraege();
  }
}

async function refresh(): Promise<void> {
  try {
    const r = await fetch("/v2-api/drucker", { credentials: "include" });
    if (!r.ok) return;
    uebernimm((await r.json()) as Stand);
  } catch {
    // Dienst startet neu oder Netz weg -- der nächste Frame holt es nach.
  }
}

let _startLauf = 0;

export function startDrucker(): void {
  if (_started) return;
  _started = true;
  const lauf = ++_startLauf;
  // Darf mitten im Zeichnen gerufen werden (Einstellungen: lies()/info()
  // eines Eintrags). Der gespeicherte Stand kommt darum erst danach in den
  // Store -- sonst bricht Svelte die ganze Seite ab (state_unsafe_mutation;
  // 21.10.2026: die Einstellungen blieben leer, sobald ein Stand im Browser
  // lag).
  queueMicrotask(() => {
    if (lauf !== _startLauf) return; // inzwischen gestoppt (oder neu gestartet)
    registerRevalidator(refresh);
    const cached = loadPersisted<Stand>(STORAGE_KEY);
    if (cached?.verbindung) _stand.set("current", cached);
    void refresh();
    _timer = setInterval(refresh, ABGLEICH_MS);
    _unsubscribe = wsSubscribe("drucker", (d) => {
      const s = d as Stand;
      if (!s || typeof s.verbindung !== "string") return;
      uebernimm(s);
    });
  });
}

export function stopDrucker(): void {
  _startLauf++;
  _started = false;
  if (_timer) clearInterval(_timer);
  _timer = undefined;
  _unsubscribe?.();
  _unsubscribe = undefined;
  _stand.clear();
  _auftraege.clear();
}

export function getDrucker(): Stand | undefined {
  return _stand.get("current");
}

// ── Verlauf ────────────────────────────────────────────────────────

export async function ladeAuftraege(): Promise<void> {
  try {
    _auftraege.set("current", (await getJSON<Auftrag[]>("/v2-api/drucker/auftraege?limit=200")) ?? []);
  } catch {
    if (!_auftraege.has("current")) _auftraege.set("current", []);
  }
}

export function getAuftraege(): Auftrag[] | undefined {
  return _auftraege.get("current");
}

// ── Steuern ────────────────────────────────────────────────────────
// Jede Aktion antwortet mit dem neuen Stand; der Drucker bestätigt kurz
// danach selbst über den Kanal.

async function steuere(pfad: string, body?: unknown): Promise<void> {
  const s = await postJSON<Stand>(`/v2-api/drucker/${pfad}`, body);
  if (s?.verbindung) uebernimm(s);
}

export const druckerLicht = (an: boolean) => steuere("licht", { an });
export const druckerErsatzspule = (an: boolean) => steuere("ersatzspule", { an });
export const druckerPause = () => steuere("pause");
export const druckerFortsetzen = () => steuere("fortsetzen");
export const druckerAbbrechen = () => steuere("abbrechen");
export const druckerVerbinden = () => steuere("verbinden");

/** Ein Filament-Profil der Bambu Studio im Homelab (internal/bambu
 *  filamente.json); id = tray_info_idx im AMS. */
export interface Sorte {
  id: string;
  name: string;
  typ: string;
  hersteller: string;
  min: number;
  max: number;
  profil: string;
}

let _sorten = $state<Sorte[] | undefined>(undefined);
let _sortenAngefragt = false;

/** Die Filament-Profile (einmal je Sitzung, sie ändern sich nur mit dem
 *  Slicer-Image). */
export function getSorten(): Sorte[] | undefined {
  if (!_sortenAngefragt) {
    _sortenAngefragt = true;
    void getJSON<Sorte[]>("/v2-api/drucker/filamente").then((l) => (_sorten = l ?? []));
  }
  return _sorten;
}

/** Sagt dem Drucker, was in einem Fach liegt (fremde Spulen ohne RFID). */
/** Wie viel auf einer fremden Spule ist (internal/bambu/spulen.go). */
export interface SpulenAngabe {
  platz?: string;
  quelle: "neu" | "geschaetzt" | "gewogen";
  rest_g?: number;
  gewogen_g?: number;
  leerspule_g?: number;
}
export const druckerBestuecken = (b: { platz: string; kennung: string; farbe: string; spule?: SpulenAngabe }) => steuere("fach", b);
export const druckerSpule = (a: SpulenAngabe & { platz: string }) => steuere("spule", a);
export const druckerSpuleWeg = (platz: string) => steuere("spule/weg", { platz });

/** Name des Zugangscodes im Geheimnis-Speicher (internal/bambu). */
export const GEHEIMNIS_ZUGANGSCODE = "bambu_zugangscode";

/** Verbindet neu und wartet, bis feststeht, ob es klappt (höchstens 15 s).
 *  Der Stand kommt per WS nach; „verbindet" ist noch kein Ergebnis. */
export async function verbindeUndWarte(): Promise<{ ok: boolean; text: string }> {
  startDrucker();
  // Ein Misserfolg zählt erst, wenn der Zustand NACH diesem Versuch gesetzt
  // wurde -- vorher steht noch der alte da (etwa „abgelehnt" vom alten Code).
  const vorher = getDrucker()?.verbindung_seit;
  await druckerVerbinden();
  for (let i = 0; i < 30; i++) {
    const s = getDrucker();
    if (s?.verbindung === "verbunden") return { ok: true, text: `verbunden mit ${s.name}` };
    if (s && s.verbindung_seit !== vorher && s.verbindung !== "verbindet" && s.verbindung !== "kein_zugangscode") {
      return { ok: false, text: s.verbindung_text ?? s.verbindung };
    }
    await new Promise((r) => setTimeout(r, 500));
  }
  const s = getDrucker();
  return { ok: false, text: s?.verbindung_text ?? "keine Antwort vom Drucker" };
}

/** Hinterlegt den Zugangscode (rein schreibend) und verbindet sofort. */
export async function zugangscodeSetzen(code: string): Promise<{ ok: boolean; text: string }> {
  await putJSON(`/v2-api/secrets/${GEHEIMNIS_ZUGANGSCODE}`, {
    value: code.trim(),
    description: "Zugangscode des 3D-Druckers (LAN-Modus)",
    readable: false,
  });
  return verbindeUndWarte();
}

/** Adresse des Vorschaubilds zu einem Schlüssel. */
export function vorschauBild(schluessel: string | undefined): string | undefined {
  return schluessel ? `/v2-api/drucker/vorschau/${encodeURIComponent(schluessel)}` : undefined;
}
